import React, { useState, useEffect } from 'react';
import { getWebAppUrl } from '../../../config/runtimeConfig';
import { Box, Avatar, Chip } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { deleteUser, getAllUsersInDB, updateUser } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import UserEditModal from './UserEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

const NoData = () => <span style={{ color: '#ccc' }}>No data</span>;

function UserList() {
  const { data: fetchedUsers, loading } = useApiData(() => getAllUsersInDB().then((res) => res.data), []);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modelOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const webAppUrl = `${getWebAppUrl()}/users/`;

  useEffect(() => {
    if (fetchedUsers) {
      setUsers(fetchedUsers);
    }
  }, [fetchedUsers]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleDelete = async (user) => {
    const UserID = user.UserID;
    try {
      setLoadingUsers(prevState => ({ ...prevState, [UserID]: true }));
      const response = await deleteUser(UserID);
      if (response.success) {
        setUsers(users.filter(u => u.UserID !== UserID));
        setSnackbarMessage('User Deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete User.');
        setSnackbarSeverity('error');
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingUsers(prevState => ({ ...prevState, [UserID]: false }));
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleSave = async (updatedUser) => {
    try {
      const response = await updateUser(updatedUser);
      if (response.success) {
        const updatedProfileImageUrl = response.data?.ProfileImageUrl || updatedUser.ProfileImageUrl;
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.UserID === updatedUser.UserID
              ? { ...user, ...updatedUser, ProfileImageUrl: updatedProfileImageUrl }
              : user
          )
        );
        setSnackbarMessage('User updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'UserID', label: 'User ID', width: 100 },
    {
      key: 'UserName',
      label: 'User Name',
      width: 200,
      render: (user) => (
        <Box display="flex" alignItems="center">
          <Avatar src={user.ProfileImageUrl} sx={{ width: 40, height: 40, marginRight: 2 }} />
          <span style={{ color: '#000' }}>
            {user.UserFirstName || user.UserLastName
              ? (`${user.UserFirstName || ''} ${user.UserLastName || ''}`.trim() || 'No data')
              : <NoData />}
          </span>
        </Box>
      ),
    },
    { key: 'UserEmail', label: 'Email', width: 250 },
    { key: 'UserPhoneNumber', label: 'Phone Number', width: 150 },
    { key: 'UserCompanyName', label: 'Company Name', width: 200 },
    {
      key: 'UserLinkedInProfileLink',
      label: 'LinkedIn',
      width: 150,
      render: (user) => (
        user.UserLinkedInProfileLink ? (
          <a
            href={user.UserLinkedInProfileLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0A66C2', fontWeight: 'bold', textDecoration: 'none' }}
          >
            <LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />
            View Profile
          </a>
        ) : (
          <NoData />
        )
      ),
    },
    {
      key: 'badgeUrl',
      label: 'Badge URL',
      width: 120,
      render: (user) => (
        user.ProfileID ? (
          <span><a href={`${webAppUrl}${user.ProfileID}`} target="_blank" rel="noopener noreferrer"><b>Click Here</b></a></span>
        ) : (
          <NoData />
        )
      ),
    },
    {
      key: 'UserIsVerified',
      label: 'Verified',
      width: 120,
      render: (user) => (
        user.UserIsVerified === true ? (
          <Chip label="Verified" color="primary" size="small" sx={{ color: 'white', backgroundColor: 'primary.main' }} />
        ) : (
          <Chip label="Unverified" color="warning" size="small" sx={{ color: 'white', backgroundColor: 'warning.main' }} />
        )
      ),
    },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Users List"
        columns={columns}
        rows={users}
        loading={loading}
        searchKeys={[(u) => `${u.UserFirstName || ''} ${u.UserLastName || ''}`, 'UserEmail', 'UserPhoneNumber', 'UserJobTitle', 'UserCompanyName']}
        rowKey="UserID"
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={loadingUsers}
      />
      <UserEditModal
        open={modelOpen}
        onClose={handleModalClose}
        user={selectedUser}
        onSave={handleSave}
      />
    </>
  );
}

export default UserList;
